import { createSlice, createAsyncThunk, PayloadAction, isAnyOf } from '@reduxjs/toolkit';
import { AuthState, LoginRequest } from '@/types/residents';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
// Admin RTK Query replaces AdminAuthAPI for login/logout and session checks
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { strings } from '@/constants/residents/strings';
import { User, UserType } from '@/types/residents/user';
import { AUTH_ERROR_MESSAGES } from '@/constants/shared/auth';
import { authApi } from '@/store/api/authApi';
import { organizationsApi } from '@/store/api/admin/organizationsApi';
import { logger } from '@/utils/common/logger';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  biometricEnabled: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue, dispatch }) => {
    try {
      // Step 1: Authenticate user
      const loginResponse = await AuthAPI.login(credentials.email, credentials.password);
      
      // Step 2: Validate response structure
      if (!loginResponse.success || 
          !loginResponse.response?.user || 
          !loginResponse.response?.accessToken) {
        return rejectWithValue('Invalid login response from server');
      }
      
      const { user, accessToken } = loginResponse.response;
      
      // Step 3: Validate user permissions
      if (user.type !== UserType.RESIDENT) {
        return rejectWithValue('Only residents are allowed to use this app');
      }
      
      // Step 4: Save credentials securely (needed for lease check)
      await SecureStorage.getInstance().saveToken(accessToken);
      
      // Step 5: Verify user has valid lease
      const leaseResponse = await AuthAPI.checkLease();
      if (!leaseResponse.success || !leaseResponse.response?.hasLease) {
        // Mirror iOS behavior: clear tokens and treat as logout on invalid lease
        try { await SecureStorage.getInstance().clearTokens(); } catch (e) { logger.warn('logoutTokenClearFailed', e); }
        return rejectWithValue('You do not have a valid lease. Please contact your property manager.');
      }
      
      return loginResponse;
    } catch (error: any) {
      // Handle different error types
      if (error.response?.data?.message) {
        return rejectWithValue(error.response.data.message);
      }
      
      if (error.code) {
        return rejectWithValue(error.message || 'Authentication failed');
      }
      
      const errorMessage = error.message || strings.error.generic;
      return rejectWithValue(errorMessage);
    }
  }
);

// Admin login async thunk
// Removed legacy adminLogin thunk in favor of RTK Query useAdminLoginMutation

// Admin logout async thunk
// Removed legacy adminLogout thunk; handled in useAdminScreen via RTK Query

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await AuthAPI.logout();
      await SecureStorage.getInstance().clearTokens();
    } catch (error: any) {
      // Logout should always succeed locally even if API fails
      console.warn('Logout API call failed:', error);
      await SecureStorage.getInstance().clearTokens();
    }
  }
);

export const refreshUser = createAsyncThunk(
  'auth/refreshUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await AuthAPI.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh user data');
    }
  }
);

// Validate session with server (use this when you need to actually check with server)
export const validateSession = createAsyncThunk(
  'auth/validateSession',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = await SecureStorage.getInstance().getToken();
      if (!token) {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      
      const state = getState() as any;
      const user = state.auth.user;
      
      // Make server call to validate session
      if (user?.roles && user.roles.length > 0) {
        const adminUser = await AuthAPI.getCurrentUser();
        return adminUser;
      } else {
        const residentUser = await AuthAPI.getCurrentUser();
        return residentUser;
      }
    } catch (error: any) {
      const errorCode = error?.code || '';
      if (errorCode === '401' || errorCode === '403') {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      return rejectWithValue(strings.error.generic);
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = await SecureStorage.getInstance().getToken();
      if (!token) {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      
      const state = getState() as any;
      const user = state.auth.user;
      
      // Follow admin-web pattern: if we have a token and persisted user, consider authenticated
      // Don't make server calls on startup to avoid triggering logout on token expiry
      if (user && token) {
        return user;
      }
      
      // If no persisted user but we have a token, reject to force re-login
      return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
    } catch (error: any) {
      return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
    }
  }
);

// Enhanced session validation for admin users
// Removed legacy validateAdminSession; session is validated via checkAuthStatus -> AuthAPI.getCurrentUser

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setBiometricEnabled: (state, action: PayloadAction<boolean>) => {
      state.biometricEnabled = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    resetAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.biometricEnabled = false;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.response!.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Login failed';
        state.isAuthenticated = false;
        state.user = null;
      });
    
    // Admin login/logout handled via RTK Query mutations and resetAuth reducer
    
    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
        state.biometricEnabled = false;
      })
      .addCase(logout.rejected, (state) => {
        // Even if logout fails, clear local state
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.biometricEnabled = false;
      });
    
    // Refresh User
    builder
      .addCase(refreshUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(refreshUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to refresh user data';
      });
    
    // Check Auth Status (local check only)
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string || 'Authentication check failed';
      });
    
    // Validate Session (server check)
    builder
      .addCase(validateSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(validateSession.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string || 'Session validation failed';
      });
    
    // RTK Query Matchers - Handle successful login/admin login/Google login/Apple login
    // This matches admin-web's pattern for automatic state management after login
    builder
      .addMatcher(
        isAnyOf(
          authApi.endpoints.adminLogin.matchFulfilled,
          authApi.endpoints.googleLogin.matchFulfilled,
          authApi.endpoints.appleLogin.matchFulfilled
        ),
        (state, action) => {
          const { payload } = action;
          
          // Validate response structure
          if (!payload?.response?.user) {
            console.error('Invalid login response structure:', payload);
            return;
          }
          
          // Update auth state with user data
          state.isLoading = false;
          state.user = payload.response.user;
          state.isAuthenticated = true;
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(
          authApi.endpoints.adminLogin.matchRejected,
          authApi.endpoints.googleLogin.matchRejected,
          authApi.endpoints.appleLogin.matchRejected
        ),
        (state) => {
          state.isLoading = false;
          state.isAuthenticated = false;
          state.user = null;
        }
      )
      // Listen for organization updates and sync to user roles
      .addMatcher(
        organizationsApi.endpoints.putOrganization.matchFulfilled,
        (state, action) => {
          // Update organization name in user's roles
          const updatedOrg = action.payload?.response;
          if (updatedOrg && state.user?.roles) {
            state.user.roles = state.user.roles.map((role: any) => {
              if (role.organization?.id === updatedOrg.id) {
                return {
                  ...role,
                  organization: {
                    ...role.organization,
                    name: updatedOrg.name,
                  }
                };
              }
              return role;
            });
          }
        }
      );
  },
});

export const { clearError, setBiometricEnabled, setUser, resetAuth } = authSlice.actions;
export default authSlice.reducer; 